// OnboardingFlow.jsx
import React from 'react';
import { useSelector } from 'react-redux';
import { ArrowLeft, CheckCircle2, LogOut } from 'lucide-react';
import { useOnboardingFacade } from '../facades/useOnboardingFacade';
import { RoleSelection, AcademicInfo } from '../components/Onboarding/OnboardingSteps';
import { SuccessStep } from '../components/Onboarding/SuccessStep'; 
import OnboardingHeader from '../components/Onboarding/OnboardingHeader';
import SubmittingStep from '../components/Onboarding/SubmittingStep';

const OnboardingFlow = () => {
  const { user } = useSelector((state) => state.user) || {};
  const { step, formData, setFormData, nextStep, prevStep, handleFinish, loading, handleSignOut } = useOnboardingFacade(user);

  // Helper to determine if we are in the final "Success" phase
  const isSuccess = step > 3;
  const isSubmitting = step === 3;

  const renderContent = () => {
    if (step === 1) return <RoleSelection formData={formData} setFormData={setFormData} />;
    if (step === 2) return <AcademicInfo formData={formData} setFormData={setFormData} />;
    if (step === 3) return <SubmittingStep onComplete={handleFinish} loading={loading} />;
    return <SuccessStep formData={formData}/>; // Step 4+: Only contains the "Go to Home" link
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-gray-50 p-4 font-sans">
      <main className="w-full max-w-md md:max-w-4xl bg-white rounded-3xl md:rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col">
        
        {/* Hide header on success screen for a cleaner look */}
        {!isSuccess && <OnboardingHeader step={step} />}

        <div className="p-8 md:p-14 flex-1 flex flex-col items-center">
          <div className="w-full space-y-12">
            
            {/* Stepper Logic - Only show if not finished */}
            {!isSuccess && (
              <div className="flex items-center justify-between max-w-[280px] mx-auto">
                {[1, 2, 3].map((s) => (
                  <React.Fragment key={s}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs transition-all ${
                      step >= s ? 'bg-blue-600 text-white ring-4 ring-blue-50' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {step > s || (isSuccess && s === 3) ? <CheckCircle2 size={18}/> : s}
                    </div>
                    {s !== 3 && <div className={`flex-1 h-1.5 mx-2 rounded-full ${step > s ? 'bg-blue-600' : 'bg-gray-100'}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}

            <div className="w-full flex justify-center">{renderContent()}</div>
          </div>

          {/* Footer Logic: Hide completely if Submitting or Success */}
          {!isSubmitting && !isSuccess && (
            <footer className="w-full mt-14 flex flex-col md:flex-row items-center justify-between gap-6">
               {step === 1 ? (
                <button 
                  onClick={handleSignOut}
                  className="flex items-center px-6 py-3 text-[10px] font-black text-red-400 hover:text-red-500 uppercase tracking-widest transition-colors"
                >
                  <LogOut size={16} className="mr-2" /> Sign Out
                </button>
              ) : (
                <button 
                  onClick={prevStep} 
                  className="flex items-center px-6 py-3 text-sm font-bold text-gray-400 hover:text-slate-600 transition-colors"
                >
                  <ArrowLeft size={18} className="mr-2" /> Back
                </button>
              )}
              
              <div className="flex flex-col md:flex-row w-full md:w-auto gap-4">
                {step === 2 && (
                   <button onClick={nextStep} className="px-6 py-3 text-sm font-bold text-gray-400">
                     Skip for now
                   </button>
                )}
                
                <button 
                  onClick={nextStep} // In Facade, if step is 2, nextStep should call the API
                  disabled={(step === 1 && !formData.role)}
                  className="w-full md:w-auto px-14 py-4 font-black rounded-2xl bg-blue-600 text-white uppercase tracking-widest text-xs shadow-lg shadow-blue-500/30"
                >
                  {step === 2 ? 'Finish Setup' : 'Continue'}
                </button>
              </div>
            </footer>
          )}
        </div>
      </main>
    </div>
  );
};

export default OnboardingFlow;